import { useCallback, useMemo, useRef } from 'react';
import {
  buildLineProximityIndex,
  findClosestLineInIndex,
  getChartPointFromEvent,
} from 'js/utils/chart-line-proximity';

const DEFAULT_MAX_DISTANCE = 12;
const DEFAULT_SAMPLES_PER_SEGMENT = 16;

/**
 * Track the closest line series to the pointer using precomputed curve segments.
 *
 * @param {Object} config
 * @param {import('js/utils/chart-line-proximity').LineSeries[]} config.series
 * @param {Function} config.x
 * @param {Function} config.y
 * @param {import('d3-shape').CurveFactory | import('d3-shape').CurveFactoryLineOnly} [config.curve]
 * @param {Function} [config.defined]
 * @param {number} [config.maxDistance=12] max pixel distance to activate a line
 * @param {{ left?: number, top?: number }} [config.margin]
 * @param {number} [config.samplesPerSegment]
 * @param {Function} [config.getLabel] maps a series entry to tooltip/label text
 * @param {Function} [config.onActiveChange] called as `(match, event)` on pointer move; `match` is null when nothing is active
 * @returns {{
 *   handlePointerMove: (event: React.MouseEvent<SVGElement>) => void,
 * }}
 */
export function useClosestLineHover({
  series,
  x,
  y,
  curve,
  defined,
  maxDistance = DEFAULT_MAX_DISTANCE,
  margin = {},
  samplesPerSegment = DEFAULT_SAMPLES_PER_SEGMENT,
  getLabel,
  onActiveChange,
}) {
  const onActiveChangeRef = useRef(onActiveChange);

  onActiveChangeRef.current = onActiveChange;

  const proximityIndex = useMemo(
    () => buildLineProximityIndex({ series, x, y, curve, defined }),
    [series, x, y, curve, defined]
  );

  const resolveMatch = useCallback(
    (event) => {
      const point = getChartPointFromEvent({
        event,
        svgElement: event.currentTarget.ownerSVGElement,
        margin,
      });

      if (!point) {
        return null;
      }

      const match = findClosestLineInIndex(proximityIndex, point, {
        maxDistance,
        samplesPerSegment,
      });

      if (match == null) {
        return null;
      }

      return {
        ...match,
        ...(getLabel ? { label: getLabel(match.series) } : {}),
      };
    },
    [proximityIndex, margin, maxDistance, samplesPerSegment, getLabel]
  );

  const handlePointerMove = useCallback(
    (event) => {
      onActiveChangeRef.current?.(resolveMatch(event), event);
    },
    [resolveMatch]
  );

  return {
    handlePointerMove,
    resolveMatch,
  };
}
